(() => {
  if (customElements.get('nordsign-file-upload')) return;
  const STOREFRONT_HOSTS = new Set(['nordsign.dk', 'www.nordsign.dk', 'international-nordsign.myshopify.com', 'nordsign.com', 'www.nordsign.com']);
  const ENDPOINT = 'https://hook.eu2.make.com/848y1pmmhp5p5h2b4jltfdyvuusa4efk';
  const EXTENSIONS = /\.(pdf|jpe?g|gif|psd|psb|indd|idml|xlsx?|docx?)$/i;
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  class NordsignFileUpload extends HTMLElement {
    connectedCallback() {
      // Enabled for draft and live themes. This visibility gate is not a substitute for Make validation.
      if (this.initialized || !['unpublished', 'main'].includes(window.Shopify?.theme?.role) ||
          !STOREFRONT_HOSTS.has(window.location.hostname) || !this.dataset.siteKey) return;
      this.initialized = true;
      this.items = [];
      this.copy = JSON.parse(this.querySelector('[data-upload-copy]').textContent);
      this.input = this.querySelector('[data-upload-input]');
      this.add = this.querySelector('[data-upload-add]');
      this.list = this.querySelector('[data-upload-list]');
      this.status = this.querySelector('[data-upload-status]');
      this.summary = this.querySelector('[data-upload-summary]');
      this.summaryTitle = this.querySelector('[data-upload-summary-title]');
      this.summaryHelp = this.querySelector('[data-upload-summary-help]');
      this.error = this.querySelector('[data-upload-error]');
      this.references = this.querySelector('[data-upload-references]');
      this.verification = this.querySelector('[data-upload-verification]');
      this.form = this.closest('form');
      this.add.addEventListener('click', () => this.input.click());
      this.input.addEventListener('change', () => {
        this.select(Array.from(this.input.files));
        this.input.value = '';
      });
      this.onSubmit = event => {
        if (!this.canSubmit()) { event.preventDefault(); event.stopImmediatePropagation(); }
      };
      this.form.addEventListener('submit', this.onSubmit, true);
      this.hidden = false;
    }

    validFile(file) {
      return typeof file.name === 'string' && file.name === file.name.trim() &&
        new TextEncoder().encode(file.name).length <= 255 && !/[\x00-\x1f\x7f/\\]/.test(file.name) &&
        file.name.lastIndexOf('.') > 0 && EXTENSIONS.test(file.name) &&
        Number.isSafeInteger(file.size) && file.size > 0 && file.size <= 200_000_000;
    }

    select(files) {
      if (this.closest('nordsign-contact-agent')?.busy) return;
      this.showError('');
      for (const file of files) {
        if (!this.validFile(file)) { this.showError(this.copy.invalid); continue; }
        if (this.items.length >= 5) { this.showError(this.copy.limit); break; }
        if (this.items.some(item => item.file?.name === file.name && item.file?.size === file.size && item.file?.lastModified === file.lastModified)) continue;
        const row = this.querySelector('[data-upload-row]').content.firstElementChild.cloneNode(true);
        const item = { file, name: file.name, size: file.size, state: 'queued', row };
        row.querySelector('[data-upload-name]').textContent = file.name;
        row.querySelector('[data-upload-size]').textContent = `${(file.size / 1_000_000).toFixed(2)} MB`;
        row.querySelector('progress').setAttribute('aria-label', file.name);
        row.querySelector('[data-upload-remove]').addEventListener('click', () => this.remove(item));
        row.querySelector('[data-upload-retry]').addEventListener('click', () => {
          if (item.state !== 'error' || this.active || this.closest('nordsign-contact-agent')?.busy) return;
          this.showError('');
          this.update(item, 'queued', this.copy.queued);
          this.pump();
        });
        this.items.push(item);
        this.list.append(row);
        this.update(item, 'queued', this.copy.queued);
      }
      this.pump();
    }

    update(item, state, message) {
      item.state = state;
      item.row.dataset.state = state;
      item.row.querySelector('[data-upload-file-status]').textContent = message;
      const progress = item.row.querySelector('progress');
      progress.hidden = state !== 'uploading';
      if (state === 'uploading') progress.value = 0;
      item.row.setAttribute('aria-busy', String(['verifying', 'preparing', 'uploading'].includes(state)));
      item.row.querySelector('[data-upload-retry]').hidden = state !== 'error';
      this.status.textContent = `${item.name}: ${message}`;
      this.refreshControls();
    }

    refreshSummary() {
      const total = this.items.length;
      this.summary.hidden = !total;
      if (!total) return;
      const complete = this.items.filter(item => item.state === 'complete').length;
      const working = this.items.some(item => ['queued', 'verifying', 'preparing', 'uploading'].includes(item.state));
      const failed = this.items.some(item => item.state === 'error');
      this.summary.dataset.state = working ? 'working' : failed ? 'error' : 'complete';
      this.summaryTitle.textContent = (working ? this.copy.summaryWorking : failed ? this.copy.summaryError : this.copy.summaryComplete)
        .replace('[complete]', complete).replace('[total]', total);
      this.summaryHelp.textContent = working ? this.copy.summaryWait : failed ? this.copy.summaryRetry : this.copy.summaryReady;
    }

    refreshControls() {
      if (!this.initialized) return;
      const busy = Boolean(this.closest('nordsign-contact-agent')?.busy);
      const unfinishedUploads = this.items.some(item => item.state !== 'complete');
      const working = this.items.some(item => ['queued', 'verifying', 'preparing', 'uploading'].includes(item.state));
      this.form.querySelectorAll('[data-continue], [data-send]').forEach(button => {
        button.disabled = busy || unfinishedUploads;
        button.dataset.uploadWaiting = String(unfinishedUploads);
        const label = button.querySelector('[data-continue-label]');
        if (label && !busy) {
          if (working) {
            if (this.continueLabel === undefined) this.continueLabel = label.textContent;
            label.textContent = this.copy.waitingButton;
          } else if (this.continueLabel !== undefined) {
            label.textContent = this.continueLabel;
            this.continueLabel = undefined;
          }
        }
      });
      this.add.disabled = busy || this.items.length >= 5;
      this.input.disabled = busy;
      for (const item of this.items) {
        item.row.querySelector('[data-upload-remove]').disabled = busy;
        item.row.querySelector('[data-upload-retry]').disabled = busy || Boolean(this.active);
      }
      this.refreshSummary();
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    remove(item) {
      if (this.closest('nordsign-contact-agent')?.busy) return;
      item.controller?.abort();
      this.items = this.items.filter(other => other !== item);
      item.file = null;
      item.row.remove();
      this.showError('');
      this.syncReferences();
      this.refreshControls();
    }

    attachments() {
      return (this.items || []).filter(item => item.state === 'complete').map(item => ({ ...item.reference }));
    }

    syncReferences() {
      const attachments = this.items.filter(item => item.state === 'complete');
      // Only read-only download links enter the email; upload grants and verification tokens never do.
      this.references.value = attachments.map(item => `${item.name}\n${item.downloadUrl}`).join('\n\n');
      this.references.disabled = !attachments.length;
    }

    canSubmit() {
      if (!(this.items || []).some(item => item.state !== 'complete')) return true;
      this.showError(this.copy.pending);
      this.error.setAttribute('tabindex', '-1');
      this.error.focus();
      return false;
    }

    async verify(signal, onInteraction = () => {}) {
      const api = await window.NordsignTurnstile.load();
      signal.throwIfAborted();
      this.verification.hidden = false;
      this.verification.dataset.interactive = 'false';
      let widget;
      let timer;
      let aborted;
      try {
        return await new Promise((resolve, reject) => {
          const fail = () => reject(new Error('verificationError'));
          aborted = fail;
          signal.addEventListener('abort', aborted, { once: true });
          timer = setTimeout(fail, 45000);
          widget = api.render(this.verification, {
            sitekey: this.dataset.siteKey, action: 'prepare_upload', size: 'compact', theme: 'light',
            appearance: 'interaction-only',
            'response-field': false, retry: 'never', 'refresh-expired': 'manual', 'refresh-timeout': 'manual',
            'before-interactive-callback': () => {
              this.verification.dataset.interactive = 'true';
              onInteraction(true);
            },
            'after-interactive-callback': () => {
              this.verification.dataset.interactive = 'false';
              onInteraction(false);
            },
            callback: token => typeof token === 'string' && token.length > 0 && token.length <= 2048 ? resolve(token) : fail(),
            'error-callback': fail, 'expired-callback': fail, 'timeout-callback': fail, 'unsupported-callback': fail
          });
        });
      } finally {
        clearTimeout(timer);
        signal.removeEventListener('abort', aborted);
        if (widget !== undefined) api.remove(widget);
        this.verification.hidden = true;
        this.verification.dataset.interactive = 'false';
      }
    }

    validateGrant(data, file) {
      if (!data || typeof data.uploadUrl !== 'string' || !UUID.test(data.uploadId || '') || typeof data.expiresAt !== 'string') throw new Error('requestError');
      const url = new URL(data.uploadUrl);
      const expiry = Date.parse(data.expiresAt);
      const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
      const allowedPath = new RegExp(`^/web-public/incoming/\\d{4}/\\d{2}/\\d{2}/${data.uploadId}\\${extension}$`);
      if (url.origin !== 'https://leonora.blob.core.windows.net' || url.username || url.password || url.hash ||
          !allowedPath.test(url.pathname) || url.searchParams.get('sr') !== 'b' || url.searchParams.get('sp') !== 'w' ||
          url.searchParams.get('spr') !== 'https' || !url.searchParams.get('sig') ||
          url.searchParams.has('comp') || url.searchParams.has('restype') ||
          !Number.isFinite(expiry) || expiry < Date.now() + 30000 || expiry > Date.now() + 31 * 60000 ||
          Date.parse(url.searchParams.get('se')) !== expiry) throw new Error('requestError');
      if (typeof data.downloadUrl !== 'string' || typeof data.downloadExpiresAt !== 'string') throw new Error('requestError');
      const downloadUrl = new URL(data.downloadUrl);
      const downloadExpiry = Date.parse(data.downloadExpiresAt);
      if (downloadUrl.origin !== url.origin || downloadUrl.pathname !== url.pathname ||
          downloadUrl.username || downloadUrl.password || downloadUrl.hash ||
          downloadUrl.searchParams.get('sr') !== 'b' || downloadUrl.searchParams.get('sp') !== 'r' ||
          downloadUrl.searchParams.get('spr') !== 'https' || !downloadUrl.searchParams.get('sig') ||
          downloadUrl.searchParams.has('comp') || downloadUrl.searchParams.has('restype') ||
          !downloadUrl.searchParams.get('rscd')?.startsWith('attachment;') ||
          !Number.isFinite(downloadExpiry) || downloadExpiry < Date.now() + 30000 ||
          downloadExpiry > Date.now() + 91 * 86400000 ||
          Date.parse(downloadUrl.searchParams.get('se')) !== downloadExpiry) throw new Error('requestError');
      return { url: url.href, expiry, downloadUrl: downloadUrl.href,
        reference: { uploadId: data.uploadId, name: file.name, size: file.size, blobPath: url.pathname.slice(1) } };
    }

    async prepare(file, botToken, signal) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 45000);
      try {
        signal.throwIfAborted();
        const type = /^[a-z0-9!#$&^_.+-]+\/[a-z0-9!#$&^_.+-]+$/i.test(file.type || '') ? file.type : 'application/octet-stream';
        const response = await fetch(ENDPOINT, {
          method: 'POST', mode: 'cors', credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer',
          signal: controller.signal, headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ version: 1, action: 'prepare_upload', requestId: crypto.randomUUID(),
            file: { name: file.name, size: file.size, contentType: type }, botToken })
        });
        if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) throw new Error('requestError');
        const text = await response.text();
        if (text.length > 10000) throw new Error('requestError');
        return this.validateGrant(JSON.parse(text), file);
      } catch { throw new Error('requestError'); }
      finally { clearTimeout(timeout); signal.removeEventListener('abort', abort); }
    }

    transfer(file, grant, signal, progress) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const abort = () => xhr.abort();
        const finish = success => {
          signal.removeEventListener('abort', abort);
          success ? resolve() : reject(new Error('transferError'));
        };
        if (signal.aborted) { finish(false); return; }
        xhr.open('PUT', grant.url);
        xhr.withCredentials = false;
        xhr.timeout = Math.max(1, Math.min(12 * 60000, grant.expiry - Date.now() - 10000));
        xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
        xhr.setRequestHeader('x-ms-version', '2023-11-03');
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.setRequestHeader('x-ms-blob-content-disposition', 'attachment');
        xhr.setRequestHeader('If-None-Match', '*');
        xhr.upload.onprogress = event => {
          if (event.lengthComputable) progress(Math.min(99, Math.floor(event.loaded / event.total * 100)));
        };
        xhr.onload = () => finish(xhr.status === 201);
        xhr.onerror = xhr.ontimeout = xhr.onabort = () => finish(false);
        signal.addEventListener('abort', abort, { once: true });
        xhr.send(file);
      });
    }

    async pump() {
      if (this.active || !this.isConnected) return;
      const item = this.items.find(item => item.state === 'queued');
      if (!item) { this.status.textContent = ''; this.refreshControls(); return; }
      this.active = item;
      item.controller = new AbortController();
      const signal = item.controller.signal;
      let stage = 'verificationError';
      try {
        this.update(item, 'verifying', this.copy.preparing);
        const token = await this.verify(signal);
        signal.throwIfAborted();
        stage = 'requestError';
        this.update(item, 'preparing', this.copy.preparing);
        const grant = await this.prepare(item.file, token, signal);
        signal.throwIfAborted();
        stage = 'transferError';
        this.update(item, 'uploading', `${this.copy.uploading} 0%`);
        await this.transfer(item.file, grant, signal, percent => {
          item.row.querySelector('progress').value = percent;
          item.row.querySelector('[data-upload-file-status]').textContent = percent === 99 ? this.copy.finishing : `${this.copy.uploading} ${percent}%`;
        });
        signal.throwIfAborted();
        item.reference = grant.reference;
        item.downloadUrl = grant.downloadUrl;
        item.file = null;
        this.update(item, 'complete', this.copy.complete);
        if (this.error.textContent === this.copy.pending && !this.items.some(other => other.state !== 'complete')) this.showError('');
        this.syncReferences();
      } catch {
        if (this.items.includes(item)) this.update(item, 'error', this.copy[stage]);
      } finally {
        item.controller = null;
        this.active = null;
        this.refreshControls();
        if (this.isConnected && this.items.some(item => item.state === 'queued')) this.pump();
      }
    }

    disconnectedCallback() {
      this.active?.controller?.abort();
      if (this.onSubmit) this.form.removeEventListener('submit', this.onSubmit, true);
    }
  }
  customElements.define('nordsign-file-upload', NordsignFileUpload);
})();
